<template>
  <div class="landing">
    <iframe
      v-if="localizeBoer"
      ref="frame"
      class="landing__frame"
      :srcdoc="boerSrcdoc"
      :title="`${appName} — landing page`"
      sandbox="allow-scripts allow-popups allow-forms"
      referrerpolicy="no-referrer"
      @load="onLoad"
    ></iframe>
    <iframe
      v-else
      ref="frame"
      class="landing__frame"
      :src="cdnFrameSrc"
      :title="`${appName} — landing page`"
      sandbox="allow-scripts allow-popups allow-forms"
      referrerpolicy="no-referrer"
      @load="onLoad"
    ></iframe>

    <!-- Insurance, not the primary path: the injected bridge draws its own
         button when the document carries no `data-metaship-signin` element.
         This one appears only when the document never reported ready — a
         failed fetch, a blocked frame — so a broken upload can never leave a
         visitor with no way in. -->
    <button v-if="showFallback" type="button" class="landing__fallback" :disabled="loading" @click="emitSignin">
      {{ loading ? t("auth.signingIn") : label }}
    </button>

    <p v-if="errorMessage" class="landing__error">{{ errorMessage }}</p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useLocale } from "@i18n/vue3";
import { LANGUAGE_OPTIONS, setHostLocale, type HostLocale } from "@/i18n/languages";

const HOST_LOCALES = new Set<string>(LANGUAGE_OPTIONS.map((l) => l.code));
import { prepareBoerLandingHtml } from "@/landing/buildBoerLanding";
import boerLandingSource from "../../landing-pages/boer.html?raw";

/** How long the document gets to run its bridge before the shell draws its
 *  own sign-in button. Long enough for a cold CDN fetch, short enough that a
 *  visitor on a broken page is not left staring at it. */
const READY_TIMEOUT_MS = 2500;

interface LandingMessage {
  source?: string;
  type?: string;
  locale?: string;
}

export default defineComponent({
  name: "CustomLanding",
  props: {
    htmlUrl: { type: String, required: true },
    appName: { type: String, default: "MetaShip" },
    primary: { type: String, default: "#649ECE" },
    /** The injected bridge's own button text (and the fallback's) — the app's sign-in label. */
    label: { type: String, default: "Sign in" },
    loading: { type: Boolean, default: false },
    errorMessage: { type: String, default: "" },
    /**
     * Boer marketing landing (theboer.co.za). The CDN upload has no CORS, so
     * the shell renders the bundled host-language document as srcdoc instead of
     * pointing the iframe at htmlUrl.
     */
    localizeBoer: { type: Boolean, default: false },
  },
  emits: ["signin"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const locale = useLocale();
    const frame = ref<HTMLIFrameElement | null>(null);
    const ready = ref(false);
    const timedOut = ref(false);
    let timer: number | undefined;

    // `primary` and `label` re-theme the injected bridge's own button, so the
    // stored document stays generic and follows the app's branding.
    const cdnFrameSrc = computed(() => {
      if (props.localizeBoer) return undefined;
      const params = new URLSearchParams({ primary: props.primary, label: props.label });
      return `${props.htmlUrl}?${params.toString()}`;
    });

    // Built once per landing (English source + current locale for first paint).
    // Later locale flips go through postMessage so the iframe does not remount.
    const boerSrcdoc = ref<string | undefined>(undefined);
    watch(
      () => props.localizeBoer,
      (on) => {
        boerSrcdoc.value = on ? prepareBoerLandingHtml(boerLandingSource, locale.value) : undefined;
      },
      { immediate: true },
    );

    const showFallback = computed(() => timedOut.value && !ready.value);

    function emitSignin(): void {
      emit("signin");
    }

    function pushLocaleToFrame(code: string): void {
      const win = frame.value?.contentWindow;
      if (!win || !props.localizeBoer) return;
      win.postMessage({ source: "metaship-shell", type: "locale", locale: code }, "*");
    }

    // The frame is sandboxed WITHOUT `allow-same-origin`, so it runs in an
    // opaque origin and always reports `event.origin === "null"` — an origin
    // check would be meaningless. Identity comes from the window itself: only
    // our own frame's contentWindow is trusted.
    function onMessage(event: MessageEvent): void {
      if (!frame.value || event.source !== frame.value.contentWindow) return;
      const data = event.data as LandingMessage | null;
      if (!data || data.source !== "metaship-landing") return;
      if (data.type === "ready") ready.value = true;
      else if (data.type === "signin") emitSignin();
      else if (data.type === "locale" && typeof data.locale === "string" && HOST_LOCALES.has(data.locale)) {
        if (data.locale !== locale.value) setHostLocale(data.locale as HostLocale);
      }
    }

    function onLoad(): void {
      // The document loaded; give its bridge a moment to report in before
      // deciding it is not going to. Clear first — a re-load (the branding
      // colour changed, so the src did) must not leave the previous timer
      // running against the new document.
      if (timer) window.clearTimeout(timer);
      timedOut.value = false;
      timer = window.setTimeout(() => (timedOut.value = true), READY_TIMEOUT_MS);
      if (props.localizeBoer) {
        ready.value = true;
        pushLocaleToFrame(locale.value);
      }
    }

    watch(locale, (code) => pushLocaleToFrame(code));

    onMounted(() => window.addEventListener("message", onMessage));
    onBeforeUnmount(() => {
      window.removeEventListener("message", onMessage);
      if (timer) window.clearTimeout(timer);
    });

    return { t, frame, cdnFrameSrc, boerSrcdoc, localizeBoer: computed(() => props.localizeBoer), showFallback, onLoad, emitSignin };
  },
});
</script>

<style scoped>
.landing {
  position: relative;
  /* NOT 100vw (DEV-1102): on any platform that reserves a classic scrollbar,
     100vw counts the scrollbar's width, so this box is WIDER than what the
     viewport can show and `overflow: hidden` clips the right edge — taking the
     right end of the landing page's header, and with it the "Start free" CTA,
     off the screen. 100% is the containing block, which is what we mean. */
  width: 100%;
  /* 100vh is the LARGEST viewport on a phone, i.e. the height with the browser
     chrome retracted. It is wrong while the address bar is showing, which is
     most of the time. dvh tracks the real one; vh stays as the fallback. */
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--tenant-background, #111927);
}
.landing__frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.landing__fallback {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 10px 20px;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: var(--tenant-primary, #649ece);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}
.landing__fallback:disabled {
  opacity: 0.6;
  cursor: default;
}
.landing__error {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  margin: 0;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: #fff;
  background: rgba(220, 76, 65, 0.92);
}
</style>
